import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingCart, Menu, X, Phone, User, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/boutique", label: "Boutique" },
  { to: "/categories", label: "Catégories" },
  { to: "/materiaux", label: "Nos matériaux" },
  { to: "/trouver-fichier-3d", label: "Trouver un fichier 3D" },
  { to: "/impression-fichier-3d", label: "Impression fichier 3D" },
  { to: "/devis", label: "Devis" },
  { to: "/contact", label: "Contact" },
];

export const Logo = ({ className = "" }) => (
  <span className={`font-marker leading-none ${className}`}>
    <span className="logo-tenay">TENAY </span>
    <span className="logo-ain">AIN-PRESSION</span>
  </span>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <Logo className="text-xl sm:text-2xl" />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-semibold text-gray-200 hover:text-[#E6007E] transition-colors whitespace-nowrap">
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a href="tel:+33635307284" data-testid="nav-call-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full btn-blue px-4 py-2 text-sm font-bold">
            <Phone className="w-4 h-4" /> Appeler
          </a>

          <Link to="/panier" data-testid="nav-cart-btn" className="relative p-2 text-white hover:text-[#E6007E] transition-colors">
            <ShoppingCart className="w-6 h-6" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#E6007E] text-white text-[11px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.role === "admin" && (
                <Button data-testid="nav-admin-btn" onClick={() => navigate("/admin")} variant="ghost"
                  className="text-white hover:text-[#0055FF]">
                  <LayoutDashboard className="w-4 h-4 mr-1" /> Admin
                </Button>
              )}
              <Button data-testid="nav-account-btn" onClick={() => navigate("/compte")} variant="ghost" className="text-white">
                <User className="w-4 h-4 mr-1" /> Compte
              </Button>
              <Button data-testid="nav-logout-btn" onClick={() => { logout(); navigate("/"); }} variant="ghost" className="text-gray-400">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Link to="/connexion" data-testid="nav-login-btn"
              className="hidden sm:inline-flex items-center rounded-full btn-primary px-4 py-2 text-sm font-bold">
              Connexion
            </Link>
          )}

          <button data-testid="nav-mobile-toggle" className="xl:hidden p-2 text-white" onClick={() => setOpen((v) => !v)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-black/95 border-t border-white/10 px-4 py-4 space-y-2">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`mnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block py-2 text-base font-semibold text-gray-200 hover:text-[#E6007E]">
              {n.label}
            </Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="btn-blue rounded-full px-4 py-2 text-center font-bold">Espace admin</Link>}
                <Link to="/compte" onClick={() => setOpen(false)} className="rounded-full border border-white/20 px-4 py-2 text-center font-bold">Mon compte</Link>
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-gray-400 py-2">Déconnexion</button>
              </>
            ) : (
              <Link to="/connexion" onClick={() => setOpen(false)} className="btn-primary rounded-full px-4 py-2 text-center font-bold">Connexion</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
